import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, message } from "antd";

import { ContractAPI } from "../../Api/ContractAPI";
import { OfficesAdminAPI } from "../../Api/OfficesAdminAPI";
import { InstitutionsAPI } from "../../Api/InstitutionsAPI";
import { PlacementAgentsAdminAPI } from "../../Api/PlacementAgentsAdminAPI";
import { TemplateAPI } from "../../Api/TemplatesAPI";
import { VehicleAPI } from "../../Api/VehicleAPI";
import { SignatureAPI } from "../../Api/SignatureAPI";
import {
	ContractDTO,
	SharesType,
	TemplateAdministrationCatalogItem,
	TemplateAdministrationData,
	TemplateAdministrationInstitutionVehicleUpsert,
	TemplateAdministrationOfficeUpsert,
	TemplateAdministrationOfficeUserUpsert,
	TemplateFieldsPreview,
	Institution,
	TemplateAdministrationSharedTemplate,
	TemplateAdministrationTemplateDocuments,
	TemplateAdministrationVehicleDocument,
	TemplateAdministrationUpsert
} from "../../Models";
import { IAdministrationProps } from "../../Components/Administration/IAdministration";

const emptyAdministrationData: TemplateAdministrationData = {
	templates: [],
	vehicles: [],
	institutions: [],
	personTypes: [],
	languages: [],
	legalAttachments: [],
	users: [],
	institutionVehicles: [],
	offices: [],
	officeUsers: [],
	vehicleDocuments: [],
	sharedTemplates: []
};

const useAdministration = ({ baseURL, baseURLExternal, clientId, translations, moduleRole }: IAdministrationProps) => {
	const templatesAPI = useMemo(() => TemplateAPI(baseURL, clientId), [baseURL, clientId]);
	const placementAgentsAPI = useMemo(() => PlacementAgentsAdminAPI(baseURL, clientId), [baseURL, clientId]);
	const officesAPI = useMemo(() => OfficesAdminAPI(baseURL, clientId), [baseURL, clientId]);
	const institutionsAPI = useMemo(() => InstitutionsAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	const contractAPI = useMemo(() => ContractAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	const vehicleAPI = useMemo(() => VehicleAPI(baseURL || "", clientId), [baseURL, clientId]);
	const signatureAPI = useMemo(() => SignatureAPI(baseURL, clientId), [baseURL, clientId]);
	const [form] = Form.useForm<TemplateAdministrationUpsert>();
	const [templateFieldsForm] = Form.useForm<{ contractId: number }>();

	const [messageApi, contextHolder] = message.useMessage();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [previewing, setPreviewing] = useState(false);
	const [previewingTemplateId, setPreviewingTemplateId] = useState<number | null>(null);
	const [templateFieldsLoading, setTemplateFieldsLoading] = useState(false);
	const [data, setData] = useState<TemplateAdministrationData>(emptyAdministrationData);
	const [previewContracts, setPreviewContracts] = useState<ContractDTO[]>([]);
	const [shareTypes, setShareTypes] = useState<SharesType[]>([]);
	const [templateFieldsPreview, setTemplateFieldsPreview] = useState<TemplateFieldsPreview | null>(null);
	const initialDataLoadStartedRef = useRef(false);
	const [templateDocumentsLoadingIds, setTemplateDocumentsLoadingIds] = useState<number[]>([]);
	const [placementAgentsDataLoaded, setPlacementAgentsDataLoaded] = useState(false);
	const [officesDataLoaded, setOfficesDataLoaded] = useState(false);
	const [shareTypesLoaded, setShareTypesLoaded] = useState(false);
	const [previewDataLoaded, setPreviewDataLoaded] = useState(false);
	const [sharedTemplatesLoaded, setSharedTemplatesLoaded] = useState(false);
	const [sharedTemplatesLoading, setSharedTemplatesLoading] = useState(false);
	const [vehicleDocumentsLoading, setVehicleDocumentsLoading] = useState(false);
	const vehicleDocumentIdsLoadedRef = useRef(new Set<number>());
	const vehicleDocumentIdsLoadingRef = useRef(new Set<number>());
	const t = (key: string, fallback: string) => translations?.[key] || fallback;

	const isReadOnly = moduleRole === "Read";

	const templateNameRules = useMemo(() => ([
		{ required: true, message: t("adminTemplateNameRequired", "El nombre de la plantilla es obligatorio") },
		{
			pattern: /^[A-Za-z0-9_-]+$/,
			message: t("adminTemplateNameInvalid", "Solo se permiten letras, números, guiones y guiones bajos")
		}
	]), [translations]);

	const mapVehiclesForSelect = useCallback((response: any): TemplateAdministrationCatalogItem[] => {
		const vehicles = response?.data ?? response ?? [];
		if (!Array.isArray(vehicles)) {
			return [];
		}

		return vehicles
			.map((vehicle: any) => ({
				id: Number(vehicle?.idVehicle ?? vehicle?.vehicleId ?? vehicle?.id ?? 0),
				name: String(vehicle?.legalName ?? vehicle?.vehicleLegalName ?? vehicle?.name ?? "")
			}))
			.filter((vehicle: TemplateAdministrationCatalogItem) => vehicle.id > 0 && !!vehicle.name)
			.sort((a: TemplateAdministrationCatalogItem, b: TemplateAdministrationCatalogItem) => a.name.localeCompare(b.name));
	}, []);

	const mapInstitutionsForSelect = useCallback((response: Institution[]): TemplateAdministrationCatalogItem[] => {
		if (!Array.isArray(response)) {
			return [];
		}

		return response
			.map((institution: Institution) => ({
				id: Number(institution?.id ?? 0),
				name: String(institution?.name ?? "")
			}))
			.filter((institution: TemplateAdministrationCatalogItem) => institution.id > 0 && !!institution.name)
			.sort((a: TemplateAdministrationCatalogItem, b: TemplateAdministrationCatalogItem) => a.name.localeCompare(b.name));
	}, []);

	const loadShareTypes = useCallback(async () => {
		if (shareTypesLoaded) {
			return;
		}

		setLoading(true);
		try {
			const shareTypesResponse = await contractAPI.getShareTypes();
			setShareTypes(Array.isArray(shareTypesResponse) ? shareTypesResponse : []);
			setShareTypesLoaded(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		} finally {
			setLoading(false);
		}
	}, [contractAPI, messageApi, shareTypesLoaded, translations.adminLoadError]);

	const loadPreviewData = useCallback(async () => {
		if (previewDataLoaded) {
			return;
		}

		try {
			const [shareTypesResponse, contractsResponse] = await Promise.all([
				contractAPI.getShareTypes(),
				contractAPI.getPreviewContracts()
			]);

			const signedContracts = Array.isArray(contractsResponse?.data)
				? contractsResponse.data.filter((contract: ContractDTO & { idContractState?: number }) => {
					const contractStateId = Number(contract.idContractState ?? contract.contract_StateId ?? 0);
					const contractStateName = String(contract.state || "").trim().toLowerCase();
					return contractStateId === 7 || contractStateName === "signed";
				})
				: [];

			setShareTypes(Array.isArray(shareTypesResponse) ? shareTypesResponse : []);
			setShareTypesLoaded(true);
			setPreviewContracts(signedContracts);
			setPreviewDataLoaded(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		}
	}, [contractAPI, messageApi, previewDataLoaded, translations.adminLoadError]);

	const loadSharedTemplatesData = useCallback(async (force = false) => {
		if ((sharedTemplatesLoaded || sharedTemplatesLoading) && !force) {
			return;
		}

		setSharedTemplatesLoading(true);
		try {
			const response = await templatesAPI.getAdministrationSharedTemplates();
			if (!response?.success) {
				throw new Error(response?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
			}

			setData(prevData => ({
				...prevData,
				sharedTemplates: Array.isArray(response.data) ? response.data : []
			}));
			setSharedTemplatesLoaded(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		} finally {
			setSharedTemplatesLoading(false);
		}
	}, [messageApi, sharedTemplatesLoaded, sharedTemplatesLoading, t, templatesAPI]);

	const loadVehicleDocumentsData = useCallback(async (idVehicle: number, force = false) => {
		if (!idVehicle || (vehicleDocumentIdsLoadedRef.current.has(idVehicle) && !force)) {
			return;
		}

		if (vehicleDocumentIdsLoadingRef.current.has(idVehicle)) {
			return;
		}

		vehicleDocumentIdsLoadingRef.current.add(idVehicle);
		setVehicleDocumentsLoading(true);
		try {
			const response = await templatesAPI.getAdministrationDFIsData(idVehicle);
			if (!response?.success) {
				throw new Error(response?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
			}

			setData(prevData => ({
				...prevData,
				vehicleDocuments: [
					...prevData.vehicleDocuments.filter(document => document.idVehicle !== idVehicle),
					...(Array.isArray(response.data?.vehicleDocuments) ? response.data.vehicleDocuments : [])
				]
			}));
			vehicleDocumentIdsLoadedRef.current.add(idVehicle);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		} finally {
			vehicleDocumentIdsLoadingRef.current.delete(idVehicle);
			setVehicleDocumentsLoading(vehicleDocumentIdsLoadingRef.current.size > 0);
		}
	}, [messageApi, t, templatesAPI]);

	const loadPlacementAgentsData = useCallback(async (force = false) => {
		if (placementAgentsDataLoaded && !force) {
			return;
		}

		setLoading(true);
		try {
			const [placementAgentsResponse, institutionsResponse] = await Promise.all([
				placementAgentsAPI.getAdministrationData(),
				institutionsAPI.getAdministrationPlacementAgentInstitutions()
			]);

			if (!placementAgentsResponse?.success) {
				throw new Error(placementAgentsResponse?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
			}

			const institutions = mapInstitutionsForSelect(institutionsResponse);
			setData(prevData => ({
				...prevData,
				institutions,
				institutionVehicles: placementAgentsResponse.data?.institutionVehicles ?? []
			}));
			setPlacementAgentsDataLoaded(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		} finally {
			setLoading(false);
		}
	}, [institutionsAPI, mapInstitutionsForSelect, messageApi, placementAgentsAPI, placementAgentsDataLoaded, translations.adminLoadError]);

	const loadOfficesData = useCallback(async (force = false) => {
		if (officesDataLoaded && !force) {
			return;
		}

		setLoading(true);
		try {
			const [officesResponse, institutionsResponse] = await Promise.all([
				officesAPI.getAdministrationData(),
				institutionsAPI.getAdministrationPlacementAgentInstitutions()
			]);

			if (!officesResponse?.success) {
				throw new Error(officesResponse?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
			}

			const institutions = mapInstitutionsForSelect(institutionsResponse);
			setData(prevData => ({
				...prevData,
				institutions,
				users: officesResponse.data?.users ?? [],
				offices: officesResponse.data?.offices ?? [],
				officeUsers: officesResponse.data?.officeUsers ?? []
			}));
			setOfficesDataLoaded(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		} finally {
			setLoading(false);
		}
	}, [institutionsAPI, mapInstitutionsForSelect, messageApi, officesAPI, officesDataLoaded, translations.adminLoadError]);

	const loadTemplatesData = useCallback(async () => {
		setLoading(true);
		try {
			const [templatesResponse, vehiclesResponse] = await Promise.all([
				templatesAPI.getAdministrationTemplatesData(),
				vehicleAPI.getVehicles()
			]);

			if (!templatesResponse?.success) {
				throw new Error(templatesResponse?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
			}

			const vehicles = mapVehiclesForSelect(vehiclesResponse);
			setData(prevData => {
				const nextData = {
					...templatesResponse.data,
					sharedTemplates: prevData.sharedTemplates ?? [],
					vehicleDocuments: prevData.vehicleDocuments ?? [],
					templates: Array.isArray(templatesResponse.data?.templates)
						? templatesResponse.data.templates.map((template: any) => ({
							...template,
							documentsLoaded: false
						}))
						: []
				};

				return {
					...nextData,
					vehicles,
					institutions: prevData.institutions,
					institutionVehicles: prevData.institutionVehicles,
					users: prevData.users,
					offices: prevData.offices,
					officeUsers: prevData.officeUsers
				};
			});
			const nextPersonTypes = Array.isArray(templatesResponse.data?.personTypes) ? templatesResponse.data.personTypes : [];
			const nextLanguages = Array.isArray(templatesResponse.data?.languages) ? templatesResponse.data.languages : [];
			form.setFieldsValue({
				idPersonType: nextPersonTypes[0]?.id,
				idLanguage: nextLanguages[0]?.id
			});
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		} finally {
			setLoading(false);
		}
	}, [form, mapVehiclesForSelect, messageApi, templatesAPI, translations.adminLoadError, vehicleAPI]);

	const loadTemplateDocuments = useCallback(async (templateId: number) => {
		const template = data.templates.find(item => item.id === templateId);
		if (!template || template.documentsLoaded || templateDocumentsLoadingIds.includes(templateId)) {
			return;
		}

		setTemplateDocumentsLoadingIds(prevState => [...prevState, templateId]);
		try {
			const response = await templatesAPI.getAdministrationTemplateDocuments(templateId);
			if (!response?.success || !response?.data) {
				throw new Error(response?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
			}

			const documents = response.data as TemplateAdministrationTemplateDocuments;
			setData(prevData => ({
				...prevData,
				templates: prevData.templates.map(item => (
					item.id === templateId
						? {
							...item,
							idMainDocument: documents.idMainDocument,
							mainDocumentUrl: documents.mainDocumentUrl,
							idEmptyMainDocument: documents.idEmptyMainDocument,
							emptyMainDocumentName: documents.emptyMainDocumentName,
							emptyMainDocumentUrl: documents.emptyMainDocumentUrl,
							idLegalAttachment: documents.idLegalAttachment,
							legalAttachmentName: documents.legalAttachmentName,
							legalAttachmentUrl: documents.legalAttachmentUrl,
							documentsLoaded: true
						}
						: item
				))
			}));
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLoadError", "No se han podido cargar los datos de administración"));
		} finally {
			setTemplateDocumentsLoadingIds(prevState => prevState.filter(id => id !== templateId));
		}
	}, [data.templates, messageApi, t, templateDocumentsLoadingIds, templatesAPI]);

	useEffect(() => {
		if (initialDataLoadStartedRef.current) {
			return;
		}

		initialDataLoadStartedRef.current = true;
		loadTemplatesData();
	}, [loadTemplatesData]);

	useEffect(() => {
		const currentPersonType = form.getFieldValue("idPersonType");
		const currentLanguage = form.getFieldValue("idLanguage");

		const nextValues: Partial<TemplateAdministrationUpsert> = {};
		if ((currentPersonType === undefined || currentPersonType === null) && data.personTypes.length > 0) {
			nextValues.idPersonType = data.personTypes[0].id;
		}

		if ((currentLanguage === undefined || currentLanguage === null) && data.languages.length > 0) {
			nextValues.idLanguage = data.languages[0].id;
		}

		if (Object.keys(nextValues).length > 0) {
			form.setFieldsValue(nextValues);
		}
	}, [data.languages, data.personTypes, form]);

	const openBlobInNewTab = useCallback((blob: Blob, title: string) => {
		const blobUrl = window.URL.createObjectURL(blob);
		const targetWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
		if (!targetWindow) {
			messageApi.warning(t("adminPopupBlocked", "Permite ventanas emergentes para abrir la preview"));
			window.URL.revokeObjectURL(blobUrl);
			return;
		}

		targetWindow.document.title = title;
		const revokeBlobUrl = () => window.URL.revokeObjectURL(blobUrl);
		targetWindow.addEventListener("beforeunload", revokeBlobUrl, { once: true });
	}, [messageApi, translations.adminPopupBlocked]);

	const handleCreateTemplate = useCallback(async (values: TemplateAdministrationUpsert) => {
		setSaving(true);
		try {
			const response = await templatesAPI.saveAdministrationTemplate({
				...values,
				idVehicle: Number(values.idVehicle),
				idPersonType: Number(values.idPersonType),
				idLanguage: Number(values.idLanguage),
				idLegalAttachment: values.idLegalAttachment ? Number(values.idLegalAttachment) : undefined
			});
			if (!response?.success) {
				throw new Error(response?.message || t("adminTemplateSaveError", "No se ha podido guardar la plantilla"));
			}

			messageApi.success(t("adminTemplateSaved", "Plantilla guardada correctamente"));
			form.resetFields();
			form.setFieldsValue({
				idPersonType: data.personTypes[0]?.id,
				idLanguage: data.languages[0]?.id
			});
			await loadTemplatesData();
		} catch (error: any) {
			messageApi.error(error?.message || t("adminTemplateSaveError", "No se ha podido guardar la plantilla"));
		} finally {
			setSaving(false);
		}
	}, [data.languages, data.personTypes, form, loadTemplatesData, messageApi, templatesAPI, translations.adminTemplateSaveError, translations.adminTemplateSaved]);

	const handleDeleteTemplate = useCallback(async (id: number) => {
		try {
			const response = await templatesAPI.deleteAdministrationTemplate(id);
			if (!response?.success) {
				throw new Error(response?.message || t("adminTemplateDeleteError", "No se ha podido eliminar la plantilla"));
			}

			messageApi.success(t("adminTemplateDeleted", "Plantilla eliminada correctamente"));
			await loadTemplatesData();
		} catch (error: any) {
			messageApi.error(error?.message || t("adminTemplateDeleteError", "No se ha podido eliminar la plantilla"));
		}
	}, [loadTemplatesData, messageApi, templatesAPI, translations.adminTemplateDeleteError, translations.adminTemplateDeleted]);

	const handleImportAdministrationFile = useCallback(async (
		kind: string,
		name: string,
		fileUrl: string,
		languageId?: number,
		idVehicle?: number,
		idShareType?: number
	) => {
		try {
			const response = await templatesAPI.importAdministrationFile(kind, name, fileUrl, languageId, idVehicle, idShareType);
			if (!response?.success) {
				throw new Error(response?.message || t("adminFileUploadError", "No se ha podido subir el documento"));
			}

			messageApi.success(t("adminFileUploadSuccess", "Documento subido correctamente"));
			await loadTemplatesData();
			if (kind === "dfi" && idVehicle) {
				vehicleDocumentIdsLoadedRef.current.delete(idVehicle);
				await loadVehicleDocumentsData(idVehicle, true);
			}

			if (kind === "static") {
				setSharedTemplatesLoaded(false);
				await loadSharedTemplatesData(true);
			}
		} catch (error: any) {
			messageApi.error(error?.message || t("adminFileUploadError", "No se ha podido subir el documento"));
		}
	}, [loadSharedTemplatesData, loadTemplatesData, loadVehicleDocumentsData, messageApi, templatesAPI, translations.adminFileUploadError, translations.adminFileUploadSuccess]);

	const handleImportStaticFile = useCallback(async (name: string, fileUrl: string, languageId?: number) => (
		handleImportAdministrationFile("static", name, fileUrl, languageId)
	), [handleImportAdministrationFile]);

	const handleImportDfiFile = useCallback(async (idVehicle: number, idShareType: number, fileUrl: string) => (
		handleImportAdministrationFile("dfi", "", fileUrl, undefined, idVehicle, idShareType)
	), [handleImportAdministrationFile]);

	const handlePreviewTemplate = useCallback(async (templateId: number, contractId: number, shareTypeId?: number) => {
		setPreviewing(true);
		setPreviewingTemplateId(templateId);
		const previewTitle = `Contract_${contractId}_Template_${templateId}_Preview.pdf`;
		const selectedShareType = shareTypes.find((item: SharesType) => item.idSharesType === shareTypeId);
		const shareTypeName = selectedShareType?.sharesType;
		const hideLoadingMessage = messageApi.open({
			type: "loading",
			content: t("adminPreviewGenerating", "Generando preview. Esta operación puede tardar unos minutos..."),
			duration: 0
		});
		try {
			const startResponse = await signatureAPI.startPreviewContract(contractId, templateId, shareTypeId, shareTypeName);
			if (!startResponse?.success || !startResponse?.data?.jobId) {
				throw new Error(startResponse?.message || t("adminPreviewError", "No se ha podido generar la preview"));
			}

			const jobId = String(startResponse.data.jobId);
			const maxAttempts = 300;
			let attempt = 0;
			let isCompleted = false;

			while (attempt < maxAttempts && !isCompleted) {
				await new Promise(resolve => window.setTimeout(resolve, 1000));
				const statusResponse = await signatureAPI.getPreviewContractStatus(jobId);
				if (!statusResponse?.success || !statusResponse?.data) {
					throw new Error(statusResponse?.message || t("adminPreviewError", "No se ha podido generar la preview"));
				}

				const status = String(statusResponse.data.status || "").toLowerCase();
				if (status === "failed") {
					throw new Error(statusResponse.data.errorMessage || t("adminPreviewError", "No se ha podido generar la preview"));
				}

				if (status === "completed") {
					isCompleted = true;
					break;
				}

				attempt += 1;
			}

			if (!isCompleted) {
				throw new Error(t("adminPreviewTimeout", "La preview sigue generándose. Vuelve a intentarlo en unos minutos."));
			}

			const blob = await signatureAPI.downloadPreviewContract(jobId, contractId, templateId);
			hideLoadingMessage();
			openBlobInNewTab(blob, previewTitle);
		} catch (error: any) {
			hideLoadingMessage();
			messageApi.error(error?.message || t("adminPreviewError", "No se ha podido generar la preview"));
		} finally {
			setPreviewing(false);
			setPreviewingTemplateId(null);
		}
	}, [messageApi, openBlobInNewTab, shareTypes, signatureAPI, t, translations.adminPreviewError]);

	const handleLoadTemplateFieldsPreview = useCallback(async (contractId: number) => {
		setTemplateFieldsLoading(true);
		try {
			const response = await templatesAPI.getTemplateFieldsPreview(contractId);
			if (!response?.success) {
				throw new Error(response?.message || t("adminTemplateFieldsLoadError", "No se han podido cargar los campos de la plantilla"));
			}

			setTemplateFieldsPreview(response.data);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminTemplateFieldsLoadError", "No se han podido cargar los campos de la plantilla"));
		} finally {
			setTemplateFieldsLoading(false);
		}
	}, [messageApi, templatesAPI]);

	const handleSaveInstitutionVehicle = useCallback(async (values: TemplateAdministrationInstitutionVehicleUpsert) => {
		try {
			const response = await placementAgentsAPI.saveAdministrationInstitutionVehicle({
				...values,
				idInstitution: Number(values.idInstitution),
				idVehicle: Number(values.idVehicle),
				maxAvailable: Number(values.maxAvailable) || 0
			});
			if (!response?.success) {
				throw new Error(response?.message || t("adminInstitutionVehicleSaveError", "No se ha podido guardar la relación institución-vehículo"));
			}

			messageApi.success(t("adminInstitutionVehicleSaved", "Relación institución-vehículo guardada correctamente"));
			await loadPlacementAgentsData(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminInstitutionVehicleSaveError", "No se ha podido guardar la relación institución-vehículo"));
		}
	}, [loadPlacementAgentsData, messageApi, placementAgentsAPI, translations.adminInstitutionVehicleSaveError, translations.adminInstitutionVehicleSaved]);

	const handleDeleteInstitutionVehicle = useCallback(async (idInstitution: number, idVehicle: number) => {
		try {
			const response = await placementAgentsAPI.deleteAdministrationInstitutionVehicle(idInstitution, idVehicle);
			if (!response?.success) {
				throw new Error(response?.message || t("adminInstitutionVehicleDeleteError", "No se ha podido eliminar la relación institución-vehículo"));
			}

			messageApi.success(t("adminInstitutionVehicleDeleted", "Relación institución-vehículo eliminada correctamente"));
			await loadPlacementAgentsData(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminInstitutionVehicleDeleteError", "No se ha podido eliminar la relación institución-vehículo"));
		}
	}, [loadPlacementAgentsData, messageApi, placementAgentsAPI, translations.adminInstitutionVehicleDeleteError, translations.adminInstitutionVehicleDeleted]);

	const handleSaveOffice = useCallback(async (values: TemplateAdministrationOfficeUpsert) => {
		try {
			const response = await officesAPI.saveAdministrationOffice({
				...values,
				id: Number(values.id) || 0,
				idInstitution: Number(values.idInstitution)
			});
			if (!response?.success) {
				throw new Error(response?.message || t("adminOfficeSaveError", "No se ha podido guardar la oficina"));
			}

			messageApi.success(t("adminOfficeSaved", "Oficina guardada correctamente"));
			await loadOfficesData(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminOfficeSaveError", "No se ha podido guardar la oficina"));
		}
	}, [loadOfficesData, messageApi, officesAPI]);

	const handleDeleteOffice = useCallback(async (id: number) => {
		try {
			const response = await officesAPI.deleteAdministrationOffice(id);
			if (!response?.success) {
				throw new Error(response?.message || t("adminOfficeDeleteError", "No se ha podido eliminar la oficina"));
			}

			messageApi.success(t("adminOfficeDeleted", "Oficina eliminada correctamente"));
			await loadOfficesData(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminOfficeDeleteError", "No se ha podido eliminar la oficina"));
		}
	}, [loadOfficesData, messageApi, officesAPI]);

	const handleSaveOfficeUser = useCallback(async (values: TemplateAdministrationOfficeUserUpsert) => {
		try {
			const response = await officesAPI.saveAdministrationOfficeUser({
				...values,
				id: Number(values.id) || 0,
				idOffice: Number(values.idOffice),
				idUser: Number(values.idUser)
			});
			if (!response?.success) {
				throw new Error(response?.message || t("adminOfficeUserSaveError", "No se ha podido guardar el usuario de oficina"));
			}

			messageApi.success(t("adminOfficeUserSaved", "Usuario de oficina guardado correctamente"));
			await loadOfficesData(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminOfficeUserSaveError", "No se ha podido guardar el usuario de oficina"));
		}
	}, [loadOfficesData, messageApi, officesAPI]);

	const handleDeleteOfficeUser = useCallback(async (id: number) => {
		try {
			const response = await officesAPI.deleteAdministrationOfficeUser(id);
			if (!response?.success) {
				throw new Error(response?.message || t("adminOfficeUserDeleteError", "No se ha podido eliminar el usuario de oficina"));
			}

			messageApi.success(t("adminOfficeUserDeleted", "Usuario de oficina eliminado correctamente"));
			await loadOfficesData(true);
		} catch (error: any) {
			messageApi.error(error?.message || t("adminOfficeUserDeleteError", "No se ha podido eliminar el usuario de oficina"));
		}
	}, [loadOfficesData, messageApi, officesAPI]);

	const getSharedTemplate = useCallback((documentKey: string, languageId: number): TemplateAdministrationSharedTemplate | undefined => {
		const documentTypeAliases = documentKey === "minorista"
			? ["Minorista"]
			: documentKey === "investor_classification"
				? ["InvestorClassification", "Investor_Classification"]
				: documentKey === "suitability_test_pt"
					? ["SuitabilityTestPT"]
				: documentKey === "convenience_test" || documentKey === "convenience_test_nav"
					? ["ConvenienceTest", "Convenience_Test", "ConvenienceTestNAV"]
					: [];
		const normalizeTemplateIdentifier = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

		return (data.sharedTemplates ?? []).find(template => {
			const normalizedType = (template.documentType || "").trim();
			const normalizedName = (template.name || "").trim().toLowerCase();
			const normalizedIdentifier = normalizeTemplateIdentifier(`${template.name || ""} ${template.url || ""}`);
			const isNavConvenienceTest = normalizedIdentifier.includes("conveniencetestnav");
			const matchesType = documentTypeAliases.includes(normalizedType);
			const matchesName = documentKey === "minorista"
				? normalizedName.startsWith("minorista")
				: documentKey === "investor_classification"
					? normalizedName.startsWith("investor_classification")
					: documentKey === "suitability_test_pt"
						? normalizedName.startsWith("teste_adequacao") || normalizedIdentifier.includes("suitabilitytestpt")
						: documentKey === "convenience_test"
						? normalizedName.startsWith("convenience_test") && !isNavConvenienceTest
						: documentKey === "convenience_test_nav"
							? isNavConvenienceTest
						: false;

			if (documentKey !== "convenience_test_nav" && template.idLanguage !== languageId) {
				return false;
			}

			if (documentKey === "convenience_test_nav") {
				return matchesName;
			}

			if (documentKey === "convenience_test" && isNavConvenienceTest) {
				return false;
			}

			return matchesType || matchesName;
		});
	}, [data.sharedTemplates]);

	const getVehicleDocument = useCallback((
		documentType: string,
		idVehicle: number,
		idShareType?: number
	): TemplateAdministrationVehicleDocument | undefined => (
		data.vehicleDocuments.find(document =>
			document.documentType === documentType
			&& document.idVehicle === idVehicle
			&& (document.idShareType ?? null) === (idShareType ?? null)
		)
	), [data.vehicleDocuments]);

	return {
		contextHolder,
		data,
		form,
		handleLoadTemplateFieldsPreview,
		loadOfficesData,
		loadPlacementAgentsData,
		loadPreviewData,
		loadSharedTemplatesData,
		loadVehicleDocumentsData,
		loadShareTypes,
		loadTemplateDocuments,
		getVehicleDocument,
		getSharedTemplate,
		handleCreateTemplate,
		handleDeleteInstitutionVehicle,
		handleDeleteOffice,
		handleDeleteOfficeUser,
		handleDeleteTemplate,
		handleImportAdministrationFile,
		handleImportDfiFile,
		handleImportStaticFile,
		handlePreviewTemplate,
		handleSaveInstitutionVehicle,
		handleSaveOffice,
		handleSaveOfficeUser,
		isReadOnly,
		loading,
		previewing,
		previewingTemplateId,
		previewContracts,
		saving,
		shareTypes,
		sharedTemplatesLoading,
		vehicleDocumentsLoading,
		templateFieldsForm,
		templateFieldsLoading,
		templateFieldsPreview,
		templateDocumentsLoadingIds,
		templateNameRules
	};
};

export default useAdministration;
